import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { msalInstance } from '../auth/msal';
import { RunCard } from '../components';
import { api } from '../lib/api';
import { Run } from '../types';

export const RunList: React.FC = () => {
  const [runs, setRuns] = useState<Run[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    const accounts = msalInstance.getAllAccounts();
    setIsAuthenticated(accounts.length > 0);

    // If not authenticated, skip API call and present friendly prompt
    if (accounts.length === 0) {
      setLoading(false);
      setRuns([]);
      setError(null); // Do not show generic failure
      return;
    }

    const fetchRuns = async () => {
      try {
        setLoading(true);
        const runs = await api.getRuns();
        setRuns(runs);
        setError(null);
      } catch (err) {
        console.error('Failed to fetch runs:', err);
        setError('Failed to load runs. Please try again.');
        setRuns([]);
      } finally {
        setLoading(false);
      }
    };

    fetchRuns();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-coffee-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading runs...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-900">☕ Coffee Runs</h1>
        {isAuthenticated && (
          <Link to="/run/new" className="btn-primary">
            + New Run
          </Link>
        )}
      </div>

      {!isAuthenticated && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
          <p className="text-yellow-800">
            Sign in to view and create coffee runs.
          </p>
        </div>
      )}

      {error && isAuthenticated && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {isAuthenticated && runs.length === 0 && !error ? (
        <div className="text-center py-12">
          <div className="text-6xl mb-4">🍵</div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            No active runs
          </h2>
          <p className="text-gray-600 mb-6">
            Start a new coffee run to get things brewing!
          </p>
          <Link to="/run/new" className="btn-primary">
            Create Your First Run
          </Link>
        </div>
      ) : (
        isAuthenticated && (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {runs.map(run => (
              <RunCard key={run.id} run={run} />
            ))}
          </div>
        )
      )}

      <div className="mt-8 text-center">
        <Link
          to="/history"
          className="text-coffee-600 hover:text-coffee-700 font-medium"
        >
          View Run History →
        </Link>
      </div>
    </div>
  );
};

export default RunList;
